import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { useViewerRole } from '@/auth/useViewerRole'
import { useTenantSlug } from '@/hooks/products/useTenantLocales'
import { ADMIN_PLUGIN_SECTIONS, type AdminPluginSection } from '@/modules/admin-sections/plugin-registry'
import {
	type AdminSectionLink,
	type AdminSectionViewer,
	visibleAdminSections,
} from '@/modules/admin-sections/visible-sections'

export type AdminSectionsState =
	| { isLoading: true }
	| { isLoading: false; viewer: AdminSectionViewer; sections: AdminSectionLink[] }

/**
 * The admin shell's menu for the signed-in viewer (TO-189 REQ-002/003/005).
 * The slug is the viewer's OWN tenant's (from `/auth/me`'s tenant id), never the
 * URL's; it is pending while that lookup runs, and unresolved (`null`) when the
 * viewer has no tenant or the lookup fails — which hides tenant-scoped sections.
 */
export function useAdminSections(plugins: readonly AdminPluginSection[] = ADMIN_PLUGIN_SECTIONS): AdminSectionsState {
	const { role, isLoading: roleLoading } = useViewerRole()
	const { permissions, tenantId, isLoading: permissionsLoading } = useViewerPermissions()
	const { slug, isError } = useTenantSlug()

	const slugPending = tenantId !== null && slug === null && !isError
	if (roleLoading || permissionsLoading || slugPending) return { isLoading: true }

	const viewer: AdminSectionViewer = { role, permissions, slug }
	return { isLoading: false, viewer, sections: visibleAdminSections(viewer, plugins) }
}
